"use client";

import React from "react";
import { motion } from "framer-motion";
import { 
  Award, 
  Users, 
  Zap, 
  TrendingUp, 
  ArrowRight,
  ShieldCheck,
  Star
} from "lucide-react";
import { Button } from "../../../components/ui/button";
import Link from "next/link";

const benefits = [
  {
    icon: <Users className="w-6 h-6" />,
    title: "Access to High-Intent Tribes",
    description: "Connect with curated groups of founders, creators, and professionals who are eager to learn and grow."
  },
  {
    icon: <TrendingUp className="w-6 h-6" />,
    title: "Scale Your Impact",
    description: "Take your teaching out of zoom calls and into serene valleys or beachside villas for deeper breakthroughs."
  },
  {
    icon: <ShieldCheck className="w-6 h-6" />,
    title: "Seamless Logistics",
    description: "We handle everything—from luxury stays to travel logistics—so you only focus on sharing your wisdom."
  },
  {
    icon: <Star className="w-6 h-6" />,
    title: "Build Your Personal Brand",
    description: "Get featured across our platforms and be recognized as a leading voice in your domain."
  }
];

const BecomeMentorPage = () => {
  return (
    <div className="bg-white min-h-screen font-jakarta">
      {/* Hero */}
      <section className="relative h-[70vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-[#063258] z-0">
          <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/50"></div>
          {/* Abstract pattern or image would go here */}
        </div>
        
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative z-10 text-center text-white px-6 max-w-4xl"
        >
          <div className="inline-flex items-center gap-2 bg-[#FFAE00] text-[#063258] px-6 py-2 rounded-full font-bold mb-8">
            <Award className="w-5 h-5" /> MENTOR PROGRAM
          </div>
          <h1 className="text-5xl md:text-7xl font-bricolage font-bold mb-8 leading-tight">
            Help the next generation of <span className="text-[#FFAE00]">leaders take flight.</span>
          </h1>
          <p className="text-xl md:text-2xl text-white/90 max-w-2xl mx-auto mb-10">
            Share your expertise, lead intentional journeys, and join an elite network of mentors shaping the future.
          </p>
          <Link href="https://forms.gle/your-form-link" target="_blank">
            <Button className="bg-[#FFAE00] hover:bg-orange-500 text-white px-12 py-8 rounded-2xl text-xl font-bold shadow-2xl transition-all flex items-center gap-3 mx-auto">
              Apply to Mentor <ArrowRight />
            </Button>
          </Link>
        </motion.div>
      </section>

      {/* Why Mentor? */}
      <section className="py-24 px-6 max-w-7xl mx-auto">
        <div className="text-center mb-20">
          <h2 className="text-4xl md:text-5xl font-bricolage font-bold text-[#063258] mb-6">Why lead an Outcome Outing?</h2>
          <p className="text-xl text-gray-500 max-w-2xl mx-auto">
            Traditional seminars are boring. We create immersive environments where true learning happens through connection, not just content.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {benefits.map((item, idx) => (
            <motion.div 
              key={idx}
              whileHover={{ scale: 1.02 }}
              className="bg-gray-50 p-8 rounded-[2rem] border border-gray-100 hover:border-[#FFAE00]/30 transition-all"
            >
              <div className="bg-[#063258] text-[#FFAE00] w-14 h-14 rounded-2xl flex items-center justify-center mb-8">
                {item.icon}
              </div>
              <h3 className="text-2xl font-bricolage font-bold text-[#063258] mb-4">{item.title}</h3>
              <p className="text-gray-600 leading-relaxed">{item.description}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Mentor Domains */}
      <section className="py-24 px-6 bg-[#063258] text-white">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div>
            <h2 className="text-4xl md:text-5xl font-bricolage font-bold mb-8">Who are we looking for?</h2>
            <p className="text-xl text-white/70 mb-10 leading-relaxed">
              We look for practitioners over theoreticians.              If you&apos;ve built something, failed at something, or mastered a craft, we want you.
            </p>
            <div className="grid grid-cols-2 gap-4">
              {[
                "Founders & Operators",
                "Product Designers",
                "Content Creators",
                "Investors & VCs",
                "Psychologists & Coaches",
                "Artists & Storytellers"
              ].map((domain, i) => (
                <div key={i} className="flex items-center gap-3 bg-white/5 p-4 rounded-xl border border-white/10 shrink-0">
                  <Zap className="w-5 h-5 text-[#FFAE00]" />
                  <span className="font-bold">{domain}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="bg-white p-10 rounded-[3rem] text-[#063258]">
            <h3 className="text-3xl font-bricolage font-bold mb-6 italic">&quot;The best way to learn is to teach. The best way to lead is to walk alongside.&quot;</h3>
            <p className="text-lg text-gray-600 mb-8 leading-relaxed">
              Outcome Outing mentors don&apos;t just stand on a podium. They trek with the tribe, share meals, and engage in late-night bonfire debates. Impact happens in the spaces between scheduled sessions.
            </p>
            <Link href="https://wa.me/917982579691" target="_blank">
              <Button variant="link" className="text-[#063258] font-bold text-lg p-0 flex items-center gap-2">
                Have questions about the program? <ArrowRight className="w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 px-6 text-center">
        <h2 className="text-3xl md:text-5xl font-bricolage font-bold text-[#063258] mb-8">Ready to curate your cohort?</h2>
        <p className="text-xl text-gray-500 mb-12 max-w-2xl mx-auto">
          Take the first step towards building a community that lasts beyond the trip.
        </p>
        <Link href="https://forms.gle/your-form-link" target="_blank">
          <Button className="bg-[#FFAE00] hover:bg-orange-500 text-white px-12 py-8 rounded-2xl text-xl font-bold shadow-xl">
            Register as a Mentor Now
          </Button>
        </Link>
      </section>
    </div>
  );
};

export default BecomeMentorPage;
